import styled from '@emotion/styled'
import { HttpStatusCode, isAxiosError } from 'axios'
import type { FC } from 'react'
import { isBrowser } from 'react-device-detect'
import { useNavigate } from 'react-router-dom'

import { routeName } from '~/@common/constants'
import { errorHandlerService } from '~/@common/services'
import authService from '~/@common/services/auth.service'
import { localStorageService } from '~/@common/services/storage.service'
import { colors } from '~/@common/styles'
import Button from '~/@common/ui/(Button)/Button/Button'

import type { FallbackProps } from './ErrorBoundary'

// isExpetedError
const ErrorFallback: FC<FallbackProps> = ({ error }) => {
  const navigate = useNavigate()

  errorHandlerService.handle(error)

  // 청크 로드 실패처럼 새 문서를 받으면 풀리는 크래시가 있어, 막다른 화면 대신 복구 수단을 하나 둔다.
  const genericErrorMessage = (
    <S.Container>
      <p>일시적인 오류가 발생했습니다. 나중에 다시 시도해주세요.</p>
      <Button theme="secondary" size="small" onClick={() => window.location.reload()}>
        새로고침
      </Button>
    </S.Container>
  )

  if (error instanceof TypeError) {
    return genericErrorMessage
  }

  switch (error.message) {
    case 'INTERNAL_SERVER_ERROR':
      return genericErrorMessage
    case 'STUDENT_WORKBOOK_NOT_FOUND':
    case 'WORKSHEET_NOT_FOUND':
      return <S.Container>더 이상 이 학습지 또는 교재가 존재하지 않습니다.</S.Container>
    case 'SELF_LEARNING_ALREADY_EXHAUSTED':
      return (
        <S.Container>더 이상 풀 수 있는 문제가 없어요. 다른 보충학습을 시도해주세요!</S.Container>
      )
    default: {
      if (isAxiosError(error) && error.response?.status === HttpStatusCode.Unauthorized) {
        // logout() 의 storage clear 전에 목적지를 캡처한다.
        // logout() 완료 시 같은 경로로 전체 이동(replace)하므로 여기 navigate 는
        // API 왕복 동안의 임시 화면 — 목적지가 갈리면 flash 가 생겨 같은 경로로 맞춘다
        const loginPath = localStorageService.loginPath ?? routeName.login
        authService.logout()

        if (isBrowser) {
          navigate(loginPath, { replace: true })
        }
        return null
      }
      return genericErrorMessage
    }
  }
}

const S = {
  Container: styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    margin: auto;
    color: ${colors.red.$400};
  `,
}

export default ErrorFallback
